import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { WhiteLabelService } from './white-label.service';
import { DeviceService } from './tv-detect.service';

@Injectable({ providedIn: 'root' })
export class UpdateService {
  company:string = '';
  constructor(
    private updates: SwUpdate,
    private wl: WhiteLabelService,
    private device: DeviceService
    ) {
    this.company = this.wl.isWhiteLabel() ? localStorage.getItem('wl_channel') : 'Toqqer';
    if (updates.isEnabled) {
      this.updates.versionUpdates
        .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
        .subscribe(() => {
          if (this.device.isTV()) {
            this.showTvUpdateModal();
          } else {
            this.showUpdatePrompt();
          }
        });
    }
  }

  private async doReload(): Promise<void> {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
    localStorage.setItem('imgCacheVersion', Date.now().toString());
    console.log('[Toqqer] 🔄 Reset image cache version for fresh assets.');
    window.location.reload();
  }

  private addKeyframes(): void {
    const style = document.createElement('style');
    style.textContent = [
      '@keyframes tvFadeIn { from { opacity: 0; } to { opacity: 1; } }',
      '@keyframes tvScaleIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }',
      '@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }',
      '@keyframes slideDown { from { transform: translateY(0); opacity: 1; } to { transform: translateY(100%); opacity: 0; } }',
      '@keyframes btnSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'
    ].join('\n');
    document.head.appendChild(style);
  }

  private showButtonLoader(btn: HTMLButtonElement): void {
    btn.disabled = true;
    btn.style.opacity = '0.8';
    btn.style.pointerEvents = 'none';
    btn.innerHTML = '';
    const spinner = document.createElement('span');
    spinner.style.cssText = `
      display: inline-block; width: 20px; height: 20px;
      border: 2.5px solid rgba(255,255,255,0.3);
      border-top-color: #fff; border-radius: 50%;
      animation: btnSpin 0.7s linear infinite;
      vertical-align: middle; margin-right: 8px;
    `;
    const text = document.createElement('span');
    text.textContent = 'Updating...';
    text.style.verticalAlign = 'middle';
    btn.append(spinner, text);
  }

  private showTvUpdateModal(): void {
    this.addKeyframes();

    // Full-screen dark overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; inset: 0; background: rgba(0,0,0,0.85);
      z-index: 999999; display: flex; align-items: center;
      justify-content: center; animation: tvFadeIn 0.3s ease-out;
    `;

    // Modal card
    const card = document.createElement('div');
    card.style.cssText = `
      background: #1a1a1a; border: 1px solid rgba(255,255,255,0.1);
      border-radius: 16px; padding: 40px 48px; text-align: center;
      max-width: 480px; width: 90%;
      box-shadow: 0 8px 32px rgba(0,0,0,0.6);
      animation: tvScaleIn 0.3s ease-out;
    `;

    // Icon
    const icon = document.createElement('div');
    icon.style.cssText = 'font-size: 48px; margin-bottom: 16px;';
    icon.textContent = '🔄';

    // Heading
    const heading = document.createElement('h2');
    heading.style.cssText = `
      color: #fff; font-family: 'Roboto', sans-serif;
      font-size: 1.4rem; font-weight: 600; margin: 0 0 12px 0;
    `;
    heading.textContent = 'Update Available';

    // Description
    const desc = document.createElement('p');
    desc.style.cssText = `
      color: #b0b0b0; font-family: 'Roboto', sans-serif;
      font-size: 1rem; margin: 0 0 32px 0; line-height: 1.5;
    `;
    const companyStrong = document.createElement('strong');
    companyStrong.style.color = '#fff';
    companyStrong.textContent = this.company;
    const okStrong = document.createElement('strong');
    okStrong.style.color = '#00c8dc';
    okStrong.textContent = 'OK';
    desc.append('A new version of ', companyStrong, ' is ready. Press ', okStrong, ' to update now.');

    // Update button (pre-focused with cyan glow)
    const btn = document.createElement('button');
    btn.style.cssText = `
      background: #0f79af; border: none; color: white;
      padding: 14px 48px; border-radius: 8px;
      font-size: 1.1rem; font-weight: 600;
      font-family: 'Roboto', sans-serif; outline: none;
      box-shadow: 0 0 0 3.5px rgba(0,200,220,0.9),
                  0 0 12px 3px rgba(0,180,200,0.5);
    `;
    btn.textContent = 'Update Now';

    card.append(icon, heading, desc, btn);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    btn.focus();

    // D-pad Enter / OK triggers reload
    const triggerUpdate = () => {
      this.showButtonLoader(btn);
      document.removeEventListener('keydown', keyHandler, true);
      this.doReload();
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault();
        e.stopPropagation();
        triggerUpdate();
      }
    };
    document.addEventListener('keydown', keyHandler, true);
    btn.addEventListener('click', () => triggerUpdate());
  }

  private showUpdatePrompt() {
    this.addKeyframes();

    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed; bottom: 0; left: 0; width: 100%;
      background: #111; color: #fff; padding: 14px 20px;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.5);
      font-family: 'Roboto', sans-serif; display: flex;
      align-items: center; justify-content: space-between;
      gap: 12px; z-index: 999999; animation: slideUp 0.35s ease-out;
    `;

    const msg = document.createElement('div');
    msg.style.cssText = 'flex: 1; font-size: 15px;';
    const strong = document.createElement('strong');
    strong.textContent = this.company;
    msg.append('🔄 A new version of ', strong, ' is available. Refresh to load latest updates.');

    const btn = document.createElement('button');
    btn.style.cssText = `
      background:#0f79af; border:none; color:white;
      padding:8px 18px; border-radius:6px; cursor:pointer;
      font-size: 14px; font-weight: 500; transition: 0.2s;
    `;
    btn.textContent = 'Reload';

    container.append(msg, btn);
    document.body.appendChild(container);

    btn.addEventListener('click', () => {
      this.showButtonLoader(btn);
      this.doReload();
    });
  }
}